import React, { useCallback } from 'react';
import { HStack, ProductBannerCard, useResponsive } from '@sg/garnish';

import type { DeliveryOrderDetailInput } from '@order/graphql';
import { CustomProductType } from '@order/graphql';
import { useNavigateToProductDetails } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import type { PartialProduct } from '../../../../../graphql/partials';

// ────────────────────────────────────────────────────────────────────────────────

export const MenuCustomCategory = (props: MenuCustomCategoryProps) => {
  const { customProducts, restaurantSlug, deliveryDetails } = props;

  const { match } = useResponsive();
  const hasMultipleProducts = customProducts.length > 1;
  const itemsPerRow = match([1, hasMultipleProducts ? 2 : 1]);

  return (
    <HStack itemsPerRow={itemsPerRow}>
      {customProducts.map((customProduct) => (
        <MenuCustomCategoryProduct
          key={customProduct.id}
          customProduct={customProduct}
          restaurantSlug={restaurantSlug}
          deliveryDetails={deliveryDetails}
        />
      ))}
    </HStack>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const MenuCustomCategoryProduct = (props: MenuCustomCategoryProductProps) => {
  const { customProduct, restaurantSlug, deliveryDetails } = props;
  const isCustomPlate = customProduct.customType === CustomProductType.Plate;

  const { t } = useLocalizationContext();

  // ─────────────────────────────────────────────────────────────────

  const title = isCustomPlate
    ? t('menu.cyo.plate.title')
    : t('menu.cyo.bowl.title');
  const description = isCustomPlate
    ? t('menu.cyo.plate.description')
    : t('menu.cyo.bowl.description');
  const ctaLabel = isCustomPlate
    ? t('menu.cyo.plate.cta')
    : t('menu.cyo.bowl.cta');
  const ctaTestID = isCustomPlate ? 'menu.cyo.plate.cta' : 'menu.cyo.bowl.cta';

  // ─── HELPERS ─────────────────────────────────────────────────────

  const navigateToProduct = useNavigateToProductDetails({
    restaurantSlug,
    deliveryAddressId: deliveryDetails?.addressId,
  });

  const onCardPress = useCallback(() => {
    navigateToProduct(customProduct);
  }, [customProduct, navigateToProduct]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <ProductBannerCard
      key={customProduct.id}
      title={title}
      description={description}
      ctaLabel={ctaLabel}
      illustration={isCustomPlate ? 'plate' : 'bowl'}
      onCtaPress={onCardPress}
      ctaTestID={ctaTestID}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type MenuCustomCategoryProps = {
  readonly customProducts: readonly PartialProduct[];
  readonly restaurantSlug: string;
  readonly deliveryDetails?: DeliveryOrderDetailInput;
};

type MenuCustomCategoryProductProps = {
  readonly customProduct: PartialProduct;
  readonly restaurantSlug: string;
  readonly deliveryDetails?: DeliveryOrderDetailInput;
};
